import { IonIcon } from '@ionic/react'
import dayjs from 'dayjs'
import { flash, leaf, logoEuro } from 'ionicons/icons'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import TimeCard from '../components/TimeCard'
import { co2PerKwh } from '../constants'
import {
	ConsumptionElementWithStats,
	useConsumptionElementData,
} from '../contexts/hooks/useConsumptionElementData'
import useUser from '../contexts/hooks/useUser'
import { trpc } from '../modules/trpc'
import { ChartTimeHook } from '../pages/electricity/charts/consumptionChart/hooks/useChartTimeConsumptionChart'
import { useChartTime } from '../pages/electricity/chartUtils/hooks/useChartTime'
import LoadingPage from '../pages/system/LoadingPage'

interface ColumnProps {
	icon: string
	value: number
	unit: string
	description: string
}

const Column: React.FC<ColumnProps> = ({ icon, value, unit, description }) => {
	return (
		<div className='column text-brand-primary-100-light dark:text-brand-primary-100-dark'>
			<IonIcon icon={icon} />
			<div className='value'>
				{value.toFixed(2)} <span className='unit'>{unit}</span>
			</div>
			<div className='desc text-brand-secondary-100-light dark:text-brand-primary-200-dark'>
				{description}
			</div>
		</div>
	)
}

interface Props {
	consumptionElement:
		| ConsumptionElementWithStats
		| {
				name: any
				totalConsumption: number
				avgExchangePrice: number
				// eslint-disable-next-line no-mixed-spaces-and-tabs
		  }
	vehicleId?: string
}

const ConsumptionElementDetailInfo: React.FC<Props> = ({ consumptionElement, vehicleId }) => {
	const { user } = useUser()

	const time = useChartTime('day')
	const data = useConsumptionElementData(time.startTime, time.endTime)
	const { t } = useTranslation('modals_consumptionElementDetailInfo')

	const { data: vehicleConsumption } =
		trpc.eNode.statistics.getUserStatisticsOnChargingSessions.useQuery(
			{
				hashedPid: user?.hashedPid ?? '',
				options: {
					startDate: dayjs(time.startTime).format(),
					type: 'vehicle',
					endDate: dayjs(time.endTime).format(),
					id: vehicleId,
				},
			},
			{
				enabled: !!user?.hashedPid && !!vehicleId && !!time.startTime && !!time.endTime,
			}
		)

	const vehicleTotals = useMemo(() => {
		if (!vehicleConsumption || vehicleConsumption.length === 0) return null

		const totalKwh = vehicleConsumption.reduce((sum, item) => sum + (item.kwhSum ?? 0), 0)
		const totalCost = vehicleConsumption.reduce((sum, item) => sum + (item.costSum ?? 0), 0)

		return {
			kwhSum: totalKwh,
			costSum: totalCost,
			co2Sum: totalKwh * co2PerKwh,
		}
	}, [vehicleConsumption])

	if (!consumptionElement) return <LoadingPage />

	return (
		<>
			<TimeCard
				loading={data.loading}
				time={{ ...time, activeTimespan: time.span } as ChartTimeHook}
				autoHeight={true}
			>
				<div className='consumption-element-detail-info !border-none'>
					<Column
						icon={flash}
						value={vehicleTotals ? vehicleTotals.kwhSum : 0}
						unit={'kWh'}
						description={t('kulutus')}
					/>
					<Column
						icon={logoEuro}
						value={vehicleTotals ? vehicleTotals.costSum : 0}
						unit={'€'}
						description={t('porssihinta')}
					/>
					<Column
						icon={leaf}
						value={vehicleTotals ? vehicleTotals.co2Sum : 0}
						unit={'kg'}
						description={`CO2`}
					/>
				</div>
			</TimeCard>
		</>
	)
}

export default ConsumptionElementDetailInfo
